var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getDOMNode } from 'libs/dom';
import { wait } from 'libs/util/time';
import { UnexpectedError } from 'libs/error/UnexpectedError';
/**
 * @class Animator
 * @since 1.0.0
 */
export class Animator {
    //--------------------------------------------------------------------------
    // Methods
    //--------------------------------------------------------------------------
    /**
     * @constructor
     * @param options
     */
    constructor(options) {
        //--------------------------------------------------------------------------
        // Methods
        //--------------------------------------------------------------------------
        /**
         * @property present
         * @since 1.0.0
         * @hidden
         */
        this.content = {};
        /**
         * @property present
         * @since 1.0.0
         * @hidden
         */
        this.present = {};
        /**
         * @property dismiss
         * @since 1.0.0
         * @hidden
         */
        this.dismiss = {};
        /**
         * @property presentAnimationState
         * @since 1.0.0
         * @hidden
         */
        this.presentAnimationState = {};
        /**
         * @property dismissAnimationState
         * @since 1.0.0
         * @hidden
         */
        this.dismissAnimationState = {};
        /**
         * @property playing
         * @since 1.0.0
         * @hidden
         */
        this.playing = false;
        /**
         * @property aborted
         * @since 1.0.0
         * @hidden
         */
        this.aborted = false;
        /**
         * @property nodes
         * @since 1.0.0
         * @hidden
         */
        this.nodes = {};
        /**
         * @property observers
         * @since 1.0.0
         * @hidden
         */
        this.observers = {};
        this.presentClassName = options.presentClassName;
        this.dismissClassName = options.dismissClassName;
        this.presentDuration = options.presentDuration;
        this.dismissDuration = options.dismissDuration;
        this.presentDelay = options.presentDelay;
        this.dismissDelay = options.dismissDelay;
        this.presentBeginHandler = options.presentBeginHandler;
        this.dismissBeginHandler = options.dismissBeginHandler;
        this.presentFinishHandler = options.presentFinishHandler;
        this.dismissFinishHandler = options.dismissFinishHandler;
        this.onBeforePresent = options.onBeforePresent;
        this.onBeforeDismiss = options.onBeforeDismiss;
        this.onAfterPresent = options.onAfterPresent;
        this.onAfterDismiss = options.onAfterDismiss;
    }
    /**
     * @property presentCount
     * @since 1.0.0
     * @hidden
     */
    get presentCount() {
        return Object.keys(this.present).length;
    }
    /**
     * @property dismissCount
     * @since 1.0.0
     * @hidden
     */
    get dismissCount() {
        return Object.keys(this.dismiss).length;
    }
    /**
     * @function manage
     * @since 1.0.0
     * @hidden
     */
    manage(transition) {
        this.transition = transition;
    }
    /**
     * @function insert
     * @since 1.0.0
     * @hidden
     */
    insert(key, component, from) {
        this.presentAnimationState[key] = AnimationState.None;
        this.present[key] = component;
        this.content[key] = component;
    }
    /**
     * @function update
     * @since 1.0.0
     * @hidden
     */
    update(key, component, from) {
        this.presentAnimationState[key] = AnimationState.None;
        this.present[key] = component;
        this.content[key] = component;
    }
    /**
     * @function dismissKey
     * @since 1.0.0
     * @hidden
     */
    remove(key, component, animator) {
        this.dismissAnimationState[key] = AnimationState.None;
        this.dismiss[key] = component;
        this.content[key] = component;
    }
    /**
     * @function start
     * @since 1.0.0
     */
    start() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.presentCount == 0 && this.dismissCount == 0) {
                return;
            }
            this.playing = true;
            yield this.play();
            this.playing = false;
        });
    }
    /**
     * @function play
     * @since 1.0.0
     */
    play() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.dismissCount)
                this.prepareDismissItems();
            if (this.presentCount)
                this.preparePresentItems();
            yield this.waitForRender();
            for (const key in this.dismiss)
                delete this.dismiss[key];
            for (const key in this.present)
                delete this.present[key];
        });
    }
    /**
     * @function prepare
     * @since 1.0.0
     */
    prepare(key, ref) {
        if (ref == null) {
            if (this.observers[key]) {
                this.observers[key].disconnect();
                delete this.observers[key];
            }
            delete this.nodes[key];
            return;
        }
        const node = getDOMNode(ref);
        if (node == null) {
            return null;
        }
        this.nodes[key] = node;
        if (this.observers[key]) {
            this.observers[key].disconnect();
            delete this.observers[key];
        }
        /*
         * Starts from a clean state
         */
        this.removeClass(node, this.presentClassName);
        this.removeClass(node, this.presentClassName + '-active');
        this.removeClass(node, this.dismissClassName);
        this.removeClass(node, this.dismissClassName + '-active');
        const presentState = this.presentAnimationState[key];
        const dismissState = this.dismissAnimationState[key];
        /*
         * Adds the proper present classes.
         */
        switch (presentState) {
            case AnimationState.Awaits:
                this.appendClass(node, this.presentClassName);
                break;
            case AnimationState.Active:
                this.appendClass(node, this.presentClassName);
                this.appendClass(node, this.presentClassName + '-active');
                break;
        }
        /*
         * Adds the proper dismiss classes.
         */
        switch (dismissState) {
            case AnimationState.Awaits:
                this.appendClass(node, this.dismissClassName);
                break;
            case AnimationState.Active:
                this.appendClass(node, this.dismissClassName);
                this.appendClass(node, this.dismissClassName + '-active');
                break;
        }
        const willAnimate = presentState != AnimationState.None &&
            dismissState != AnimationState.None;
        if (willAnimate) {
            this.observers[key] = this.createObserver(key);
        }
    }
    /**
     * @function preparePresentItems
     * @since 1.0.0
     * @hidden
     */
    preparePresentItems() {
        return __awaiter(this, void 0, void 0, function* () {
            for (const key in this.present) {
                this.content[key] = this.present[key];
                this.transition.insertContent(key, this.present[key]);
            }
            yield this.waitForRender();
        });
    }
    /**
     * @function prepareDismissItems
     * @since 1.0.0
     * @hidden
     */
    prepareDismissItems() {
        return __awaiter(this, void 0, void 0, function* () {
            for (const key in this.dismiss) {
                this.content[key] = this.dismiss[key];
                this.transition.insertContent(key, this.dismiss[key]);
            }
            yield this.waitForRender();
        });
    }
    /**
     * @function abort
     * @since 1.0.0
     */
    abort() {
        this.aborted = true;
        for (const key in this.observers) {
            this.observers[key].disconnect();
        }
        this.observers = {};
    }
    /**
     * @function dismissItems
     * @since 1.0.0
     * @hidden
     */
    dismissItems() {
        return __awaiter(this, void 0, void 0, function* () {
            return (this.dismissCount &&
                Promise.all(Object.keys(this.dismiss).map((key) => this.dismissItem(key))));
        });
    }
    /**
     * @function presentItems
     * @since 1.0.0
     * @hidden
     */
    presentItems() {
        return __awaiter(this, void 0, void 0, function* () {
            return (this.presentCount &&
                Promise.all(Object.keys(this.present).map((key) => this.presentItem(key))));
        });
    }
    /**
     * @function presentItem
     * @since 1.0.0
     * @hidden
     */
    presentItem(key) {
        return __awaiter(this, void 0, void 0, function* () {
            const node = this.nodes[key];
            if (node == null) {
                return;
            }
            if (this.onBeforePresent) {
                this.onBeforePresent(node);
            }
            yield this.waitForPresentToBegin(key, node);
            if (this.aborted) {
                return;
            }
            this.observe(key, false);
            switch (this.presentAnimationState[key]) {
                case AnimationState.Awaits:
                    yield this.appendClass(node, this.presentClassName);
                    yield this.appendClass(node, this.presentClassName + '-active', true);
                    break;
                case AnimationState.Active:
                    yield this.appendClass(node, this.presentClassName);
                    yield this.appendClass(node, this.presentClassName + '-active');
                    break;
            }
            this.observe(key, true);
            this.presentAnimationState[key] = AnimationState.Active;
            yield this.waitForPresentToFinish(key, node);
            if (this.aborted) {
                return;
            }
            this.presentAnimationState[key] = AnimationState.Finished;
            this.finishPresentAnimation(key, node);
            if (this.onAfterPresent) {
                this.onAfterPresent(node);
            }
        });
    }
    /**
     * @function dismissItem
     * @since 1.0.0
     * @hidden
     */
    dismissItem(key) {
        return __awaiter(this, void 0, void 0, function* () {
            const node = this.nodes[key];
            if (node == null) {
                return;
            }
            if (this.onBeforeDismiss) {
                this.onBeforeDismiss(node);
            }
            yield this.waitForDismissToBegin(key, node);
            if (this.aborted) {
                return;
            }
            this.observe(key, false);
            switch (this.dismissAnimationState[key]) {
                case AnimationState.Awaits:
                    yield this.appendClass(node, this.dismissClassName);
                    yield this.appendClass(node, this.dismissClassName + '-active', true);
                    break;
                case AnimationState.Active:
                    yield this.appendClass(node, this.dismissClassName);
                    yield this.appendClass(node, this.dismissClassName + '-active');
                    break;
            }
            this.observe(key, true);
            this.dismissAnimationState[key] = AnimationState.Active;
            yield this.waitForDismissToFinish(key, node);
            if (this.aborted) {
                return;
            }
            this.dismissAnimationState[key] = AnimationState.Finished;
            this.finishDismissItemTransition(key, node);
            if (this.onAfterDismiss) {
                this.onAfterDismiss(node);
            }
        });
    }
    /**
     * @method finishPresentAnimation
     * @since 1.0.0
     * @hidden
     */
    finishPresentAnimation(key, node) {
        this.removeObserver(key);
        this.removeClass(node, this.presentClassName);
        this.removeClass(node, this.presentClassName + '-active');
        delete this.presentAnimationState[key];
        delete this.present[key];
    }
    /**
     * @method finishDismissItem
     * @since 1.0.0
     * @hidden
     */
    finishDismissItemTransition(key, node) {
        this.removeObserver(key);
        this.transition.removeContent(key);
        delete this.dismissAnimationState[key];
        delete this.dismiss[key];
        delete this.content[key];
    }
    /**
     * @method appendClass
     * @since 1.0.0
     * @hidden
     */
    appendClass(node, name, reflow = false) {
        if (node.classList.contains(name) == true) {
            return Promise.resolve();
        }
        if (reflow) {
            return new Promise((success) => {
                requestAnimationFrame(() => {
                    node.scrollTop;
                    node.classList.add(name);
                    success();
                });
            });
        }
        node.classList.add(name);
        return Promise.resolve();
    }
    /**
     * @method removeClass
     * @since 1.0.0
     * @hidden
     */
    removeClass(node, name, reflow = false) {
        if (node.classList.contains(name) == false) {
            return Promise.resolve();
        }
        if (reflow) {
            return new Promise((success) => {
                requestAnimationFrame(() => {
                    node.scrollTop;
                    node.classList.remove(name);
                    success();
                });
            });
        }
        node.classList.remove(name);
        return Promise.resolve();
    }
    /**
     * @function createObserver
     * @since 1.0.0
     * @hidden
     */
    createObserver(key) {
        const node = this.nodes[key];
        if (node == null) {
            throw new UnexpectedError();
        }
        /**
         * @function onMutate
         * @since 1.0.0
         * @hidden
         */
        const onMutate = (e) => {
            const node = this.nodes[key];
            if (node == null) {
                return;
            }
            const presentState = this.presentAnimationState[key];
            const dismissState = this.dismissAnimationState[key];
            this.observe(key, false);
            switch (presentState) {
                case AnimationState.Awaits:
                    this.appendClass(node, this.presentClassName);
                    break;
                case AnimationState.Active:
                    this.appendClass(node, this.presentClassName);
                    this.appendClass(node, this.presentClassName + '-active');
                    break;
            }
            switch (dismissState) {
                case AnimationState.Awaits:
                    this.appendClass(node, this.dismissClassName);
                    break;
                case AnimationState.Active:
                    this.appendClass(node, this.dismissClassName);
                    this.appendClass(node, this.dismissClassName + '-active');
                    break;
            }
            this.observe(key, true);
        };
        const observer = new MutationObserver(onMutate);
        const options = {
            attributes: true,
            attributeFilter: ['class'],
            characterData: false,
            childList: false
        };
        observer.observe(node, options);
        return observer;
    }
    /**
     * @function removeObserver
     * @since 1.0.0
     * @hidden
     */
    removeObserver(key) {
        if (this.observers[key]) {
            this.observers[key].disconnect();
        }
        delete this.observers[key];
    }
    /**
     * @function observe
     * @since 1.0.0
     * @hidden
     */
    observe(key, enable) {
        const observer = this.observers[key];
        if (observer == null) {
            return;
        }
        if (enable) {
            const node = this.nodes[key];
            if (node == null) {
                return;
            }
            const options = {
                attributes: true,
                attributeFilter: ['class'],
                characterData: false,
                childList: false
            };
            observer.observe(node, options);
        }
        else {
            observer.disconnect();
        }
    }
    /**
     * @function waitForRender
     * @since 1.0.0
     * @hidden
     */
    waitForRender() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((callback) => {
                this.transition.callback = callback;
                //requestAnimationFrame(callback)
            });
        });
    }
    /**
     * @function waitForDismissToBegin
     * @since 1.0.0
     * @hidden
     */
    waitForDismissToBegin(key, node) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.dismissBeginHandler) {
                yield this.dismissBeginHandler(key, node);
            }
            else if (this.dismissDelay) {
                yield wait(this.dismissDelay);
            }
        });
    }
    /**
     * @function waitForDismissToFinish
     * @since 1.0.0
     * @hidden
     */
    waitForDismissToFinish(key, node) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.dismissFinishHandler) {
                yield this.dismissFinishHandler(key, node);
            }
            else if (this.dismissDuration) {
                yield wait(this.dismissDuration);
            }
        });
    }
    /**
     * @function waitForPresentToBegin
     * @since 1.0.0
     * @hidden
     */
    waitForPresentToBegin(key, node) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.presentBeginHandler) {
                yield this.presentBeginHandler(key, node);
            }
            else if (this.presentDelay) {
                yield wait(this.presentDelay);
            }
        });
    }
    /**
     * @function waitForPresentToFinish
     * @since 1.0.0
     * @hidden
     */
    waitForPresentToFinish(key, node) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.presentFinishHandler) {
                yield this.presentFinishHandler(key, node);
            }
            else if (this.presentDuration) {
                yield wait(this.presentDuration);
            }
        });
    }
}
/**
 * @enum AnimationState
 * @since 1.0.0
 * @hidden
 */
export var AnimationState;
(function (AnimationState) {
    AnimationState[AnimationState["None"] = 0] = "None";
    AnimationState[AnimationState["Awaits"] = 1] = "Awaits";
    AnimationState[AnimationState["Active"] = 2] = "Active";
    AnimationState[AnimationState["Finished"] = 3] = "Finished";
})(AnimationState || (AnimationState = {}));
